import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import './ListPage.css';
import PostCard from './PostCard';
import PostModal from './PostModal';
import { Post as PostData } from './HomePage';
import { Loader } from "@googlemaps/js-api-loader";
import { List, SelectedRestaurant } from '../types/list';

interface Restaurant {
    id: string;
    name: string;
    address: string;
}

export default function ListPage() {
    const { listId } = useParams<{ listId: string }>();
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [list, setList] = useState<List | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'posts' | 'restaurants'>('posts');
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const [editingBlurb, setEditingBlurb] = useState<string>('');
    const [isMapsApiLoaded, setIsMapsApiLoaded] = useState(false);
    const autocompleteContainerRef = useRef<HTMLDivElement>(null);
    const [selectedRestaurant, setSelectedRestaurant] = useState<SelectedRestaurant | null>(null);
    const [isAddingRestaurant, setIsAddingRestaurant] = useState(false);
    const [posts, setPosts] = useState<{ [key: string]: PostData }>({});
    const [restaurants, setRestaurants] = useState<{ [key: string]: Restaurant }>({});
    const [selectedPost, setSelectedPost] = useState<PostData | null>(null);

    // Load Google Maps API
    useEffect(() => {
        // Only load Maps API if user is the owner of the list
        if (!list || list.user_id !== currentUser?.uid) {
            return;
        }

        const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
        if (!googleMapsApiKey) {
            console.error("Google Maps API Key is missing.");
            setError("Restaurant search is unavailable. Missing API Key.");
            return;
        }

        const loader = new Loader({
            apiKey: googleMapsApiKey,
            version: "weekly",
            libraries: ["places"],
        });

        loader.load()
            .then(() => setIsMapsApiLoaded(true))
            .catch(e => {
                console.error("Error loading Google Maps API:", e);
                setError("Failed to load restaurant search functionality.");
            });
    }, [list, currentUser]);

    // Initialize Autocomplete Element
    useEffect(() => {
        // Only initialize if user is the owner of the list
        if (!list || list.user_id !== currentUser?.uid) {
            return;
        }

        console.log('Autocomplete useEffect triggered. isMapsApiLoaded:', isMapsApiLoaded, 'activeTab:', activeTab);
        // Only run if the API is loaded, the ref container exists, AND the correct tab is active
        if (!isMapsApiLoaded || !autocompleteContainerRef.current || activeTab !== 'restaurants') {
            console.log('Autocomplete useEffect: Exiting early. isMapsApiLoaded:', isMapsApiLoaded, 'Ref:', !!autocompleteContainerRef.current, 'activeTab:', activeTab);
            return;
        }
        console.log('Autocomplete useEffect: Proceeding with initialization. Ref exists and correct tab is active.');

        // Ensure the container is empty before appending
        autocompleteContainerRef.current.innerHTML = '';
        console.log('Autocomplete useEffect: Container cleared.');

        const google = window.google;

        if (!google?.maps?.places?.PlaceAutocompleteElement) {
            console.error("PlaceAutocompleteElement not available.");
            setError("Restaurant search feature is currently unavailable.");
            return;
        }
        console.log('Autocomplete useEffect: PlaceAutocompleteElement is available.');

        try {
            const placeAutocompleteElement = new google.maps.places.PlaceAutocompleteElement({
                requestedLanguage: 'en',
                types: ['establishment']
            });
            console.log('Autocomplete useEffect: PlaceAutocompleteElement created.');

            // Add styling
            placeAutocompleteElement.style.width = '100%';
            placeAutocompleteElement.style.padding = '8px';
            placeAutocompleteElement.style.borderRadius = '0.375rem';
            placeAutocompleteElement.style.border = '1px solid #d1d5db';
            placeAutocompleteElement.style.outline = 'none';
            placeAutocompleteElement.style.boxSizing = 'border-box';

            // Append the web component to the container
            autocompleteContainerRef.current.appendChild(placeAutocompleteElement);
            console.log('Autocomplete useEffect: PlaceAutocompleteElement appended to container.');
            placeAutocompleteElement.setAttribute('placeholder', 'Search for a restaurant...');

            // Add event listener for place selection
            placeAutocompleteElement.addEventListener('gmp-select', async (event: Event) => {
                console.log('Place selection event received:', event);
                console.log('Event type:', event.type);
                console.log('Event target:', event.target);

                const selectEvent = event as CustomEvent<{ placePrediction: google.maps.places.PlacePrediction | null }>;
                const placePrediction = selectEvent.detail?.placePrediction;

                if (!placePrediction) {
                    // Attempting a potential workaround for different event structures
                    const directPlacePrediction = (event as any).placePrediction as google.maps.places.PlacePrediction | null;
                    console.log('Direct placePrediction:', directPlacePrediction);
                    if (!directPlacePrediction) {
                        console.error('No place prediction found in the selection event (checked detail and direct property)');
                        setSelectedRestaurant(null);
                        return;
                    }
                    await handlePlaceSelection(directPlacePrediction);
                    return;
                }

                await handlePlaceSelection(placePrediction);
            });
            console.log('Autocomplete useEffect: Event listener added.');
        } catch (err) {
            console.error('Autocomplete useEffect: Error during initialization:', err);
            setError('Failed to initialize restaurant search.');
        }

    }, [isMapsApiLoaded, activeTab, list, currentUser]);

    const handlePlaceSelection = async (placePrediction: google.maps.places.PlacePrediction) => {
        const place = placePrediction.toPlace();
        try {
            const fields = await place.fetchFields({
                fields: ['id', 'displayName', 'formattedAddress', 'types', 'primaryType', 'location', 'addressComponents']
            });

            if (fields.place.id && fields.place.displayName && fields.place.formattedAddress) {
                const location = fields.place.location;
                let city = '', postcode = '', country = '';
                const addressComponents = fields.place.addressComponents || [];

                for (const component of addressComponents as any) {
                    const componentLongName = component.Fg;
                    const componentTypes = component.Eg || [];

                    if (componentTypes.includes('locality') || componentTypes.includes('postal_town')) {
                        city = componentLongName;
                    } else if (componentTypes.includes('postal_code')) {
                        postcode = componentLongName;
                    } else if (componentTypes.includes('country')) {
                        country = componentLongName;
                    }
                }

                const restaurantData = {
                    placeId: fields.place.id,
                    name: fields.place.displayName,
                    address: fields.place.formattedAddress,
                    city,
                    postcode,
                    country,
                    primaryType: fields.place.primaryType || undefined,
                    latitude: location ? location.lat() : undefined,
                    longitude: location ? location.lng() : undefined
                };

                setSelectedRestaurant(restaurantData);
                await addRestaurantToList(restaurantData);
            }
        } catch (error) {
            console.error('Error processing place details:', error);
            setError('Failed to process restaurant details');
        }
    };

    const addRestaurantToList = async (restaurantData: SelectedRestaurant) => {
        if (!currentUser || !listId) return;

        setIsAddingRestaurant(true);
        try {
            const token = await currentUser.getIdToken();
            
            // First, check if restaurant exists
            const checkResponse = await fetch(`${API_BASE_URL}/api/restaurants/place/${restaurantData.placeId}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            let restaurantId;
            if (checkResponse.status === 404) {
                // Create new restaurant
                const createResponse = await fetch(`${API_BASE_URL}/api/restaurants`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(restaurantData),
                });
                if (!createResponse.ok) throw new Error('Failed to create restaurant');
                const newRestaurant = await createResponse.json();
                restaurantId = newRestaurant.id;
            } else {
                const existingRestaurant = await checkResponse.json();
                restaurantId = existingRestaurant.id;
            }

            // Add restaurant to list
            const addToListResponse = await fetch(`${API_BASE_URL}/api/lists/${listId}/items`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    itemId: restaurantId,
                    type: 'restaurant',
                    blurb: restaurantData.blurb
                }),
            });

            if (!addToListResponse.ok) throw new Error('Failed to add restaurant to list');

            // Refresh list data
            fetchList();
            setSelectedRestaurant(null);
        } catch (err) {
            setError('Failed to add restaurant to list');
            console.error('Error adding restaurant:', err);
        } finally {
            setIsAddingRestaurant(false);
        }
    };

    const fetchList = async () => {
        if (!listId) return;
        setLoading(true);
        setError(null);
        try {
            const headers: HeadersInit = {};
            if (currentUser) {
                const token = await currentUser.getIdToken();
                headers['Authorization'] = `Bearer ${token}`;
            }
            
            const response = await fetch(`${API_BASE_URL}/api/lists/${listId}`, {
                headers,
            });
            
            if (response.status === 404) {
                throw new Error('List not found');
            } else if (response.status === 403) {
                throw new Error('You do not have permission to view this list');
            } else if (!response.ok) {
                throw new Error(`Failed to fetch list (${response.status})`);
            }
            
            const data = await response.json();
            setList(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load list');
            console.error('Error fetching list:', err);
            setList(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, [currentUser, listId]);

    // Set initial active tab based on list content
    useEffect(() => {
        if (list) {
            const postCount = list.metadata.items.filter(item => item.type === 'post').length;
            const restaurantCount = list.metadata.items.filter(item => item.type === 'restaurant').length;
            
            if (postCount === 0 && restaurantCount > 0) {
                setActiveTab('restaurants');
            }
        }
    }, [list]);

    const handleDeleteList = async () => {
        if (!currentUser || !listId) return;
        if (!window.confirm('Are you sure you want to delete this list? This action cannot be undone.')) return;

        setLoading(true);
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_BASE_URL}/api/lists/${listId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });
            
            if (response.status === 404) {
                throw new Error('List not found');
            } else if (response.status === 403) {
                throw new Error('You do not have permission to delete this list');
            } else if (!response.ok) {
                throw new Error(`Failed to delete list (${response.status})`);
            }
            
            navigate('/profile');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete list');
            console.error('Error deleting list:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateItemBlurb = async (itemId: string, newBlurb: string) => {
        if (!currentUser || !listId) return;
        
        setLoading(true);
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_BASE_URL}/api/lists/${listId}/items/${itemId}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ blurb: newBlurb }),
            });
            
            if (response.status === 404) {
                throw new Error('List or item not found');
            } else if (response.status === 403) {
                throw new Error('You do not have permission to update this item');
            } else if (response.status !== 204) {
                throw new Error(`Failed to update item note (${response.status})`);
            }
            
            // Update local state
            setList(prevList => {
                if (!prevList) return null;
                const updatedItems = prevList.metadata.items.map(item => 
                    item.id === itemId ? { ...item, blurb: newBlurb } : item
                );
                return {
                    ...prevList,
                    metadata: { ...prevList.metadata, items: updatedItems }
                };
            });
            setEditingItemId(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update item note');
            console.error('Error updating item blurb:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteItem = async (itemId: string) => {
        if (!currentUser || !listId) return;
        if (!window.confirm('Are you sure you want to remove this item from the list?')) return;
        
        setLoading(true);
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_BASE_URL}/api/lists/${listId}/items/${itemId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });
            
            if (response.status === 404) {
                throw new Error('List or item not found');
            } else if (response.status === 403) {
                throw new Error('You do not have permission to remove this item');
            } else if (response.status !== 204) {
                throw new Error(`Failed to remove item (${response.status})`);
            }
            
            // Update local state
            setList(prevList => {
                if (!prevList) return null;
                const updatedItems = prevList.metadata.items.filter(item => item.id !== itemId);
                return {
                    ...prevList,
                    metadata: { ...prevList.metadata, items: updatedItems }
                };
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to remove item');
            console.error('Error removing item:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPostDetails = async (postId: string) => {
        // Don't fetch if we already have the data
        if (posts[postId]) return;

        try {
            const headers: HeadersInit = {};
            if (currentUser) {
                const token = await currentUser.getIdToken();
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE_URL}/api/foodposts/${postId}`, {
                headers,
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch post details');
            }
            
            const postData: PostData = await response.json();
            setPosts(prev => ({ ...prev, [postId]: postData }));
        } catch (err) {
            console.error('Error fetching post details:', err);
            // Set a placeholder to indicate error state
            setPosts(prev => ({
                ...prev,
                [postId]: {
                    id: postId,
                    title: 'Error loading post',
                    content: 'Please try refreshing the page',
                    created_at: '',
                    user_id: '',
                    user_name: '',
                    user_display_name: '',
                    yum_count: 0,
                    is_yummed: false
                }
            }));
        }
    };

    const handlePostClick = (postId: string, e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('.action-button')) {
            return;
        }
        
        const postToShow = posts[postId];
        if (postToShow) {
            setSelectedPost(postToShow);
        }
    };

    const handleCloseModal = () => {
        setSelectedPost(null);
    };

    // Update restaurants state when list changes
    useEffect(() => {
        if (list) {
            const restaurantItems = list.metadata.items.filter(item => item.type === 'restaurant');
            const restaurantMap: { [key: string]: Restaurant } = {};
            
            restaurantItems.forEach(item => {
                if (item.name && item.address) {
                    restaurantMap[item.id] = {
                        id: item.id,
                        name: item.name,
                        address: item.address
                    };
                }
            });
            
            setRestaurants(restaurantMap);
        }
    }, [list]);

    if (loading) {
        return (
            <div className="list-page">
                <div className="loading">Loading...</div>
            </div>
        );
    }

    if (error || !list) {
        return (
            <div className="list-page">
                <div className="error-message">
                    {error || 'List not found'}
                </div>
            </div>
        );
    }

    const postCount = list?.metadata?.items?.filter(item => item.type === 'post').length || 0;
    const restaurantCount = list?.metadata?.items?.filter(item => item.type === 'restaurant').length || 0;

    return (
        <div className="list-page">
            <div className="list-header">
                <div className="list-title-section">
                    <h1>{list?.name}</h1>
                </div>
                <div className="header-right">
                    <Link to={`/profile/u/${list?.user_name}`} className="list-owner">
                        <img 
                            src={list?.avatar_url || '/default-avatar.png'} 
                            alt={`${list?.display_name}'s avatar`}
                            className="owner-avatar"
                        />
                        <span className="owner-name">
                            {list?.display_name}
                        </span>
                    </Link>
                    <div className="list-actions">
                        {list?.user_id === currentUser?.uid && (
                            <button 
                                className="delete-button"
                                onClick={handleDeleteList}
                            >
                                Delete List
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="list-tabs">
                <button
                    className={`tab-button ${activeTab === 'posts' ? 'active' : ''}`}
                    onClick={() => setActiveTab('posts')}
                >
                    Posts ({postCount})
                </button>
                <button
                    className={`tab-button ${activeTab === 'restaurants' ? 'active' : ''}`}
                    onClick={() => setActiveTab('restaurants')}
                >
                    Restaurants ({restaurantCount})
                </button>
            </div>

            <div className="list-content">
                {activeTab === 'posts' ? (
                    <div className="posts-grid">
                        {postCount === 0 ? (
                            <div className="empty-state">
                                <p>No posts in this list yet.</p>
                                <p className="empty-state-subtext">Add posts to your list to see them here!</p>
                            </div>
                        ) : (
                            list?.metadata?.items
                                ?.filter(item => item.type === 'post')
                                .map(item => {
                                    // Fetch post details if we don't have them yet
                                    if (!posts[item.id]) {
                                        fetchPostDetails(item.id);
                                    }
                                    
                                    return (
                                        <div key={item.id} className="post-card">
                                            <PostCard
                                                post={posts[item.id] || {
                                                    id: item.id,
                                                    title: item.title || '',
                                                    content: item.content || '',
                                                    created_at: item.created_at || '',
                                                    user_id: item.user_id || '',
                                                    user_name: '',
                                                    user_display_name: '',
                                                    yum_count: 0,
                                                    is_yummed: false,
                                                    first_photo: item.first_photo || undefined
                                                }}
                                                onCardClick={handlePostClick}
                                            />
                                            <div className="item-actions">
                                                {editingItemId === item.id ? (
                                                    <div className="edit-blurb">
                                                        <textarea
                                                            value={editingBlurb}
                                                            onChange={(e) => setEditingBlurb(e.target.value)}
                                                            placeholder="Add a note about this post..."
                                                            className="blurb-input"
                                                        />
                                                        <div className="blurb-buttons">
                                                            <button
                                                                className="save-button"
                                                                onClick={() => handleUpdateItemBlurb(item.id, editingBlurb)}
                                                            >
                                                                Save
                                                            </button>
                                                            <button
                                                                className="cancel-button"
                                                                onClick={() => {
                                                                    setEditingItemId(null);
                                                                    setEditingBlurb('');
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        {item.blurb && (
                                                            <div className="item-blurb">
                                                                <p>{item.blurb}</p>
                                                            </div>
                                                        )}
                                                        {list?.user_id === currentUser?.uid && (
                                                            <div className="item-controls">
                                                                <button
                                                                    className="edit-button"
                                                                    onClick={() => {
                                                                        setEditingItemId(item.id);
                                                                        setEditingBlurb(item.blurb || '');
                                                                    }}
                                                                >
                                                                    {item.blurb ? 'Edit Note' : 'Add Note'}
                                                                </button>
                                                                <button
                                                                    className="delete-button"
                                                                    onClick={() => handleDeleteItem(item.id)}
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                        )}
                    </div>
                ) : (
                    <div className="restaurants-grid">
                        {list?.user_id === currentUser?.uid && (
                            <div className="add-restaurant-section">
                                <h3>Add a Restaurant</h3>
                                <div ref={autocompleteContainerRef} className="restaurant-search-container">
                                    {!isMapsApiLoaded && !error && (
                                        <div className="loading-indicator">
                                            <div className="loading-spinner"></div>
                                            <span>Loading restaurant search...</span>
                                        </div>
                                    )}
                                </div>
                                {selectedRestaurant && (
                                    <div className="selected-restaurant">
                                        <svg className="location-icon" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                        </svg>
                                        <div className="restaurant-info">
                                            <span className="restaurant-name">{selectedRestaurant.name}</span>
                                            <span className="restaurant-address">{selectedRestaurant.address}</span>
                                        </div>
                                        <div className="restaurant-blurb">
                                            <textarea
                                                placeholder="Add a note about this restaurant..."
                                                value={selectedRestaurant.blurb || ''}
                                                onChange={(e) => setSelectedRestaurant({
                                                    ...selectedRestaurant,
                                                    blurb: e.target.value
                                                })}
                                                className="blurb-input"
                                                rows={3}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {restaurantCount === 0 ? (
                            <div className="empty-state">
                                <p>No restaurants in this list yet.</p>
                                <p className="empty-state-subtext">Add restaurants to your list to see them here!</p>
                            </div>
                        ) : (
                            list?.metadata?.items
                                ?.filter(item => item.type === 'restaurant')
                                .map(item => (
                                    <div key={item.id} className="restaurant-card">
                                        <div className="restaurant-info">
                                            <h3 className={!restaurants[item.id]?.name ? 'loading' : ''}>
                                                {restaurants[item.id]?.name || 'Loading...'}
                                            </h3>
                                            <a 
                                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurants[item.id]?.name + ' ' + restaurants[item.id]?.address)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="restaurant-address-link"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <p className={!restaurants[item.id]?.address ? 'loading' : ''}>
                                                    {restaurants[item.id]?.address || ''}
                                                </p>
                                            </a>
                                        </div>
                                        <div className="item-actions">
                                            {editingItemId === item.id ? (
                                                <div className="edit-blurb">
                                                    <textarea
                                                        value={editingBlurb}
                                                        onChange={(e) => setEditingBlurb(e.target.value)}
                                                        placeholder="Add a note about this restaurant..."
                                                        className="blurb-input"
                                                    />
                                                    <div className="blurb-buttons">
                                                        <button
                                                            className="save-button"
                                                            onClick={() => handleUpdateItemBlurb(item.id, editingBlurb)}
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            className="cancel-button"
                                                            onClick={() => {
                                                                setEditingItemId(null);
                                                                setEditingBlurb('');
                                                            }}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    {item.blurb && (
                                                        <div className="item-blurb">
                                                            <p>{item.blurb}</p>
                                                        </div>
                                                    )}
                                                    {list?.user_id === currentUser?.uid && (
                                                        <div className="item-controls">
                                                            <button
                                                                className="edit-button"
                                                                onClick={() => {
                                                                    setEditingItemId(item.id);
                                                                    setEditingBlurb(item.blurb || '');
                                                                }}
                                                            >
                                                                {item.blurb ? 'Edit Note' : 'Add Note'}
                                                            </button>
                                                            <button
                                                                className="delete-button"
                                                                onClick={() => handleDeleteItem(item.id)}
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))
                        )}
                    </div>
                )}
            </div>
            {selectedPost && (
                <PostModal
                    post={selectedPost}
                    onClose={handleCloseModal}
                />
            )}
        </div>
    );
} 